import { useMemo } from 'react';

import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSubscribers } from '@/modules/broadcast/hooks/useSubscribers';
import {
  selectBroadcast,
  selectContextVariableOptions,
  selectIsDraft,
} from '@/modules/broadcast/redux/selectors';
import { getContextVariableOptions } from '@/modules/broadcast/utils';
import { Form, WhatsappTemplateComponent } from '@/shared/types/broadcast';
import { contextVariableWithBraces, urlPattern } from '@/util/validation/patterns';

interface Props {
  isReadOnly: boolean;
  templateHeader: WhatsappTemplateComponent;
}

export const rules = (t) => ({
  id: {
    required: t('validation.required'),
    validate: (value: string) => {
      if (contextVariableWithBraces.test(value)) {
        return true;
      }
    },
  },
  link: {
    required: t('validation.required'),
    validate: (value: string) => {
      return (
        urlPattern.test(value) ||
        contextVariableWithBraces.test(value) ||
        t('validation.invalid_url')
      );
    },
  },
});

export const HeaderMediaSection = ({ isReadOnly }: Props) => {
  const { t } = useTranslation();
  const { control, setValue, clearErrors, unregister } = useFormContext<Form>();
  const { errors } = useFormState({ control });

  const isDraft = useSelector(selectIsDraft);
  const broadcast = useSelector(selectBroadcast);
  const { subscribers } = useSubscribers(broadcast.broadcast_id);
  const draftSubscribersOptions = useSelector(selectContextVariableOptions);
  const storedSubscribersOptions = getContextVariableOptions(subscribers?.pages?.[0].subscribers);
  const rawOptions = isDraft ? draftSubscribersOptions : storedSubscribersOptions;

  // Transform options to use 'category' instead of 'type' for FormAutocomplete
  const options: AutocompleteOption[] = useMemo(() => {
    return rawOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: t(`auto_complete.group_labels.${option.type}`),
    }));
  }, [rawOptions, t]);

  const selectOptions = [
    { label: t('common.link'), value: 'link' },
    { label: 'WhatsApp ID', value: 'id' },
  ];

  const mediaType = useWatch({ control, name: 'headerMediaParam.type' });

  // Dynamically update the rules and value based on the mediaType
  const handleTypeChange = (newType: 'link' | 'id') => {
    setValue('headerMediaParam.type', newType);
    setValue('headerMediaParam.value', null);
    clearErrors('headerMediaParam.value'); // Clear any errors
    unregister('headerMediaParam.value'); // Unregister the field to avoid persisting the old validation
  };

  return (
    <div className="mb-4">
      <div className="my-2 flex flex-wrap gap-4">
        <div className="w-40">
          <FormField
            control={control}
            name="headerMediaParam.type"
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('home.content')}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleTypeChange(value as 'link' | 'id');
                    }}
                    value={field.value || ''}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      size="sm"
                      className={errors.headerMediaParam?.type ? 'border-destructive' : ''}
                    >
                      <SelectValue placeholder={t('common.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {selectOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col items-start">
          <FormAutocomplete<Form>
            key={mediaType} // Forces the component to re-render with the new type
            control={control}
            options={options}
            name="headerMediaParam.value"
            label={t('common.type')}
            placeholder={t('broadcasts.url_id_or_variable_placeholder')}
            rules={{ ...rules(t)[mediaType] }} // Apply dynamic rules based on mediaType
            error={!!errors.headerMediaParam?.value}
            errorMessage={errors.headerMediaParam?.value?.message}
            disabled={isReadOnly || !mediaType}
          />
        </div>
      </div>
    </div>
  );
};
